import React, { useEffect, useMemo, useState } from "react";

import { faCircleInfo } from "@performyard/fontawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@performyard/fontawesome/react-fontawesome";
import { CheckboxGroup, ModalBase, RadioButtons } from "@performyard/py-components";

import Button from "../../../../components/Button";
import DeletableChip from "../../../../components/Chip/templates/DeletableChip";
import Checkbox from "../../../../components/Inputs/Checkbox";
import Input from "../../../../components/Inputs/InputText";
import InputText from "../../../../components/Inputs/InputText";
import Spacer from "../../../../components/Spacer";
import Typography from "../../../../components/Text";

import { useAiSettings } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import { getVisibilityOptions, useIsCompanyDocumentPage } from "../../helpers";
import { DocumentVisibility } from "../../schema";
import { EditDocumentModalProps } from "../../schema";

const EditDocumentModal = ({
	show,
	onClose,
	userData,
	onEdit,
	document,
	isProcessing,
	isEmbeddingLimitReached,
}: EditDocumentModalProps) => {
	const isCompanyDocumentPage = useIsCompanyDocumentPage();
	const isCompanyDocument = document?.vis_to_everyone !== undefined;
	const isDocumentPdf =
		(!document?.content_type || document?.content_type === "application/pdf") &&
		document?.filename.toLowerCase().endsWith(".pdf");

	// Check if AI features should be shown (requires both AI FF and Conversational AI FF)
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: conversationalAIFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-conversational-ui-enabled", false);
	const { data: aiSettings } = useAiSettings();
	const isAiEnabled =
		aiFeatureFlagEnabled &&
		conversationalAIFeatureFlagEnabled &&
		aiSettings?.conversationalAiSettings?.askHrEnabled;

	// Memoize visibility options to prevent unnecessary recalculations
	const visibilityOptions = useMemo(() => {
		if (!userData?.employeeName) {
			return [];
		}
		return getVisibilityOptions(isCompanyDocument, userData.employeeName);
	}, [isCompanyDocument, userData?.employeeName]);

	// Initialize with empty array to prevent undefined state
	const [docVisibilities, setDocVisibilities] = useState<DocumentVisibility[]>([]);
	const [fileName, setFileName] = useState("");
	const [tagValue, setTagValue] = useState("");
	const [tags, setTags] = useState<string[]>([]);
	const [allowAiRetrieval, setAllowAiRetrieval] = useState<boolean>(document?.allow_ai_retrieval ?? false);
	const isSignedDocument = Array.isArray(document?.signers);

	useEffect(() => {
		setFileName(document?.filename || "");
		setTags(document?.tags || []);
		setTagValue("");
		setAllowAiRetrieval(document?.allow_ai_retrieval ?? false);
		// update the visibility options based on the document's visibility
		if (document && visibilityOptions.length > 0) {
			if (isCompanyDocument) {
				// For company files: determine if Everyone or Administrators
				if (document.vis_to_everyone === true) {
					// Everyone can see it
					setDocVisibilities([visibilityOptions.find((opt) => opt.value === "vis_to_everyone")!]);
				} else {
					// Only administrators can see it
					setDocVisibilities([visibilityOptions.find((opt) => opt.value === "vis_to_man")!]);
				}
			} else {
				// For user files, use the existing logic
				let updatingVisibilities = [...visibilityOptions];
				if (document.vis_to_emp === false) {
					updatingVisibilities = updatingVisibilities.filter((option) => option.value !== "vis_to_emp");
				}
				if (document.vis_to_man === false) {
					updatingVisibilities = updatingVisibilities.filter((option) => option.value !== "vis_to_m");
				}
				// if the document has signers
				if (Array.isArray(document.signers)) {
					updatingVisibilities = [];
				}
				setDocVisibilities(updatingVisibilities);
			}
		} else if (visibilityOptions.length > 0) {
			// Fallback: set to default visibility options if document is not available yet
			setDocVisibilities(visibilityOptions);
		}
	}, [document, visibilityOptions, isCompanyDocument]);

	const handleSaveChange = async () => {
		if (onEdit && document?.id) {
			await onEdit(document.id, fileName, docVisibilities, tags, allowAiRetrieval);
		}
		setFileName("");
		setDocVisibilities([]);
	};

	const handleAddTag = () => {
		if (tagValue.trim() === "") {
			setTagValue("");
			return;
		}
		setTags((prev) => [...prev.filter((t) => t !== tagValue), tagValue]);
		setTagValue("");
	};

	const content = (
		<div>
			<div className="text-center">
				<h3 className="m-0 text-xl font-semibold">Edit Document</h3>
			</div>
			<Spacer height={2} />
			<div style={{ textAlign: "left" }}>
				<Typography size="paragraph-3" weight="semi-bold">
					File Name
				</Typography>
				<Spacer height={1} />
				<Input
					value={fileName}
					onChange={(val) => {
						setFileName(val);
					}}
				/>
			</div>
			{isCompanyDocument && (
				<>
					<Spacer height={2} />
					<div style={{ textAlign: "left" }}>
						<Typography size="paragraph-3" weight="semi-bold">
							Tags
						</Typography>
						<Spacer height={1} />
						<div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
							<div style={{ flex: 1 }}>
								<InputText
									onChange={(value) => setTagValue(value)}
									value={tagValue}
									placeholder="Press 'enter' or click '+Add' button"
									onEnter={handleAddTag}
								/>
							</div>
							<Button color="blue" size="medium" onClick={handleAddTag}>
								+ Add
							</Button>
						</div>
						<Spacer height={1} />
						{tags.length > 0 &&
							tags.map((tag) => (
								<DeletableChip
									key={tag}
									label={tag}
									onDelete={() => setTags((prev) => [...prev.filter((t) => t !== tag)])}
								/>
							))}
					</div>
				</>
			)}
			{!isSignedDocument && (
				<>
					<Spacer height={2} />
					<div style={{ textAlign: "left" }}>
						<Typography size="paragraph-3" weight="semi-bold">
							Who can see this file?
						</Typography>
						<Spacer height={1} />
						{isCompanyDocument ? (
							<RadioButtons
								id="edit-document-visibility"
								value={docVisibilities[0]?.value || ""}
								options={visibilityOptions}
								onChange={(val) => {
									const selectedOption = visibilityOptions.find((opt) => opt.value === val);
									if (selectedOption) {
										setDocVisibilities([selectedOption]);
									}
								}}
							/>
						) : (
							<CheckboxGroup
								id="edit-document-visibility"
								value={docVisibilities.map((dv) => dv.value)}
								options={visibilityOptions}
								onChange={(val) => {
									const selectedOptions = visibilityOptions.filter((opt) => val.includes(opt.value));
									setDocVisibilities(selectedOptions);
								}}
							/>
						)}
					</div>
				</>
			)}
			{isCompanyDocumentPage && isCompanyDocument && isAiEnabled && isDocumentPdf && (
				<>
					<Spacer height={2} />
					<div style={{ display: "flex", alignItems: "flex-start", gap: "8px", textAlign: "left" }}>
						<Checkbox
							value={allowAiRetrieval}
							onChange={setAllowAiRetrieval}
							disabled={isProcessing || (isEmbeddingLimitReached && !allowAiRetrieval)}
						/>
						<Typography size="paragraph-3" weight="regular">
							Allow Ask PerformYard feature to retrieve and reference this document when answering
							questions
						</Typography>
					</div>
					{isProcessing && (
						<>
							<Spacer height={1} />
							<div style={{ display: "flex", alignItems: "center", gap: "8px", textAlign: "left" }}>
								<FontAwesomeIcon icon={faCircleInfo} />
								<Typography size="paragraph-3" weight="regular">
									This document is currently being processed for use by Ask PerformYard
								</Typography>
							</div>
						</>
					)}
					{isEmbeddingLimitReached && !allowAiRetrieval && (
						<>
							<Spacer height={1} />
							<div style={{ display: "flex", alignItems: "center", gap: "8px", textAlign: "left" }}>
								<FontAwesomeIcon icon={faCircleInfo} />
								<Typography size="paragraph-3" weight="regular">
									Maximum Number of Documents Processing. Please wait until a document has finished
									processing to enable Ask PerformYard for another document
								</Typography>
							</div>
						</>
					)}
				</>
			)}
			<Spacer height={3} />
			<div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
				<Button size="large" onClick={onClose}>
					Cancel
				</Button>
				<Button color="green" size="large" onClick={handleSaveChange}>
					Save Changes
				</Button>
			</div>
		</div>
	);

	return (
		<ModalBase isOpen={show} onClose={onClose}>
			<div style={{ width: "416px", padding: "0 24px 24px 24px" }}>{content}</div>
		</ModalBase>
	);
};

export default EditDocumentModal;
